import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  IconButton, InputAdornment, Stack,
  Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableActionCell from "components/table/TableActionCell";
import ClickableTableRow, { stopRowClickPropagation } from "components/table/ClickableTableRow";
import TableTimeChips from "components/table/TableTimeChips";
import TableStatusChip from "components/table/TableStatusChip";
import api from "utils/api";
import { useToast } from "contexts/ToastContext";

function formatTime(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export default function ProductCategoryList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, showApiError } = useToast();

  const params = new URLSearchParams(location.search);
  const store_id = params.get("store_id") || "";
  const store_name = params.get("store_name") || "";

  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [storeStatus, setStoreStatus] = useState(true);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (store_id) fetchStoreStatus();
  }, [store_id]);

  const fetchStoreStatus = async () => {
    try {
      const res = await api.get(`/store/${store_id}`);
      setStoreStatus(res.data?.data?.status ?? true);
    } catch (err) {
      console.error("fetchStoreStatus error:", err);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await api.get("/product/category/list", {
        params: { store_id },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setFiltered(data);
    } catch (err) {
      console.error(err);
      setRows([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = search.toLowerCase().trim();
    setFiltered(
      !q
        ? rows
        : rows.filter(
          (r) =>
            r.name_en?.toLowerCase().includes(q) ||
            r.name_ar?.toLowerCase().includes(q)
        )
    );
  }, [search, rows]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/product/category/delete/${deleteTarget.id}`);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (cat) => {
    try {
      await api.patch(`/product/category/toggle-status/${cat.id}`);
      setRows((prev) =>
        prev.map((r) =>
          r.id === cat.id ? { ...r, status: !r.status } : r
        )
      );
    } catch (err) {
      showApiError(err, "Failed to toggle status");
    }
  };

  const handleCreate = () =>
    navigate(
      `/product/category/create?store_id=${store_id}&store_name=${encodeURIComponent(store_name)}`
    );

  const handleEdit = (cat) =>
    navigate(
      `/product/category/edit/${cat.id}?store_id=${store_id}&store_name=${encodeURIComponent(store_name)}`
    );

  const handleViewProducts = (cat) =>
    navigate(
      `/product/list?store_id=${store_id}&category_id=${cat.id}&category_name=${encodeURIComponent(cat.name_en)}&store_name=${encodeURIComponent(store_name)}`
    );

  const getImageUrl = (cat) => cat?.Doc?.imageUrl || null;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        variant="nested"
        onBack={() => navigate("/store/list")}
        backTitle="Back to stores"
        title="Product Categories"
        subtitle={
          store_name
            ? `Store: ${decodeURIComponent(store_name)} — organize menu categories.`
            : "Organize menu categories for this store."
        }
        count={filtered.length}
        titleExtra={
          !storeStatus ? (
            <Chip label="Store Inactive" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
          ) : null
        }
        actionLabel="Add Category"
        actionIcon={AddIcon}
        onAction={handleCreate}
      />

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name (EN / AR)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 420 }}
        />
      </Box>

      <MainCard contentSX={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
            <Typography variant="h6">
              {search
                ? `No categories match "${search}"`
                : "No categories found for this store"}
            </Typography>
            {!search && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={handleCreate}
              >
                Create First Category
              </Button>
            )}
          </Box>
        ) : (
          <ScrollableTableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  {["#", "Image", "Name", "Arabic Name", "Times", "Status", "Actions"].map(
                    (col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          bgcolor: "grey.50",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          py: 1.5,
                        }}
                      >
                        {col}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((cat, idx) => {
                  const imageUrl = getImageUrl(cat);
                  const times = cat.times || [];

                  return (
                    <ClickableTableRow
                      key={cat.id}
                      sx={{ verticalAlign: "middle" }}
                      onClick={() => handleViewProducts(cat)}
                    >

                      {/* # */}
                      <TableCell sx={{ color: "text.disabled", fontSize: 13 }}>
                        {idx + 1}
                      </TableCell>

                      {/* Image */}
                      <TableCell>
                        <Avatar
                          src={imageUrl || undefined}
                          variant="rounded"
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "primary.lighter",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {!imageUrl && cat.name_en?.[0]?.toUpperCase()}
                        </Avatar>
                      </TableCell>

                      {/* Name EN */}
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {cat.name_en}
                        </Typography>
                      </TableCell>

                      {/* Name AR */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ direction: "rtl", color: "text.secondary" }}
                        >
                          {cat.name_ar || "—"}
                        </Typography>
                      </TableCell>

                      {/* Times */}
                      <TableCell>
                        <TableTimeChips times={times} formatTime={formatTime} />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <TableStatusChip
                          active={!!cat.status}
                          onClick={stopRowClickPropagation(() => handleToggleStatus(cat))}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableActionCell>
                        <TableActions>
                          <TableActionButton
                            action="view"
                            title="View Products"
                            onClick={() => handleViewProducts(cat)}
                          />
                          <TableActionButton action="edit" onClick={() => handleEdit(cat)} />
                          <TableActionButton
                            action="delete"
                            onClick={() => setDeleteTarget({ id: cat.id, name: cat.name_en })}
                          />
                        </TableActions>
                      </TableActionCell>

                    </ClickableTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        )}
      </MainCard>

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            size="small"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: '#b71c1c',
              color: '#fff',
              '&:hover': { bgcolor: '#8e0000' },
              '&.Mui-disabled': { bgcolor: 'rgba(183, 28, 28, 0.45)', color: '#fff' }
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}